import { TitleCasePipe } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { AppDataService } from '../../core/services/app-data.service';
import { MaintenanceService } from '../../core/services/maintenance.service';
import { ExportService } from '../../core/services/export.service';
import { RepairHistoryEntry } from '../../core/models/maintenance.models';
import {
  currentTimestamp,
  formatCurrency,
  formatDate,
} from '../../core/utils/format.util';
import { confirmAction } from '../../core/utils/confirm.util';
import { environment } from '../../environments/environment';
import { AuthService } from '../auth/auth.service';

@Component({
  selector: 'app-repair-history',
  standalone: true,
  imports: [FormsModule, TitleCasePipe],
  templateUrl: './repair-history.component.html',
  styleUrl: './repair-history.component.css',
})
export class RepairHistoryComponent implements OnInit {
  private readonly maintenanceService = inject(MaintenanceService);
  protected readonly auth = inject(AuthService);
  private readonly appData = inject(AppDataService);
  private readonly exporter = inject(ExportService);

  readonly timestamp = currentTimestamp();
  readonly formatDate = formatDate;
  readonly formatCurrency = formatCurrency;

  readonly searchQuery = signal('');
  readonly statusFilter = signal('');
  readonly typeFilter = signal('');
  readonly showMoreFilters = signal(false);
  readonly completedFrom = signal('');
  readonly completedTo = signal('');
  readonly technicianFilter = signal('');

  ngOnInit(): void {
    if (!environment.useMockData) {
      this.appData.refreshAll();
    }
  }

  readonly historyStats = computed(() => {
    const list = this.maintenanceService.allRepairHistory();
    return {
      total: list.length,
      completed: list.filter((row) => row.status === 'completed').length,
      warranty: list.filter((row) => row.status === 'warranty').length,
    };
  });

  readonly filteredHistory = computed(() => {
    const search = this.searchQuery().trim().toLowerCase();
    const status = this.statusFilter();
    const type = this.typeFilter();
    const completedFrom = this.completedFrom();
    const completedTo = this.completedTo();
    const technician = this.technicianFilter().trim().toLowerCase();

    return this.maintenanceService.allRepairHistory().filter((entry) => {
      if (status && entry.status !== status) {
        return false;
      }
      if (type && entry.repairType !== type) {
        return false;
      }
      if (technician && !entry.technician.toLowerCase().includes(technician)) {
        return false;
      }
      if (completedFrom && entry.completedDate < completedFrom) {
        return false;
      }
      if (completedTo && entry.completedDate > completedTo) {
        return false;
      }
      if (
        search &&
        !entry.jobId.toLowerCase().includes(search) &&
        !entry.assetName.toLowerCase().includes(search) &&
        !entry.description.toLowerCase().includes(search)
      ) {
        return false;
      }
      return true;
    });
  });

  readonly showViewModal = signal(false);
  readonly selectedRecord = signal<RepairHistoryEntry | null>(null);

  readonly showToast = signal(false);
  readonly toastMessage = signal('');

  openViewModal(record: RepairHistoryEntry): void {
    this.selectedRecord.set(record);
    this.showViewModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  closeViewModal(): void {
    this.showViewModal.set(false);
    this.selectedRecord.set(null);
    document.body.style.overflow = 'auto';
  }

  closeModalOnBackdrop(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal-overlay')) {
      this.closeViewModal();
    }
  }

  deleteEntry(entry: RepairHistoryEntry): void {
    if (!this.auth.hasPermission('delete_orders')) {
      return;
    }
    if (
      !confirmAction(
        `Delete repair history entry "${entry.jobId}"? This cannot be undone.`,
      )
    ) {
      return;
    }

    if (!this.maintenanceService.deleteRepairHistoryEntry(entry.id)) {
      this.showSuccessToast(
        'Repair history delete is not available via the API yet.',
      );
      return;
    }

    this.showSuccessToast('Repair history entry deleted successfully');
  }

  showSuccessToast(message: string): void {
    this.toastMessage.set(message);
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }

  closeToast(): void {
    this.showToast.set(false);
  }

  exportData(): void {
    this.exporter.downloadCsv(
      `repair-history-${new Date().toISOString().slice(0, 10)}`,
      this.filteredHistory().map((entry) => ({ ...entry })),
    );
  }

  printPage(): void {
    this.exporter.printPage();
  }

  toggleMoreFilters(): void {
    this.showMoreFilters.update((open) => !open);
  }

  clearMoreFilters(): void {
    this.completedFrom.set('');
    this.completedTo.set('');
    this.technicianFilter.set('');
  }
}
